/**
 * 浏览器放弃一条 SSE 之后，由我们自己接回来。
 *
 * # 为什么需要这一层
 *
 * `EventSource` 的自动重连**只覆盖一半的断法**。HTML 规范把响应分两类：
 *
 *   网络层断掉 / EOF（连接被掐、进程没了）  → reestablish：readyState 回到
 *                                             CONNECTING，浏览器自己接回来
 *   **拿到了响应但它不对**（status ≠ 200、   → fail the connection：readyState
 *   Content-Type 不是 text/event-stream）      落到 CLOSED，`error` 只发一次，
 *                                             **从此不再重试**
 *
 * 第二类在生产上一点都不罕见——它就是控制面重启那几秒的 502。
 * 2026-09-01 生产 nginx 访问日志把整个过程记全了：
 *
 *   13:42:54  六条流同时 EOF（control 容器下线）
 *   13:42:57  浏览器自己重连一次 —— 全部 502
 *   之后      **一条重连都没有了**
 *
 * 那一刻之后，页面上所有靠这条流吃饭的东西就永久停在「断了」那一档，
 * 而别的请求（轮询、点开一个页面）照样 200——**一个哪儿看着都对、
 * 只有实时那一块是死的页面**，直到用户手动刷新为止。
 *
 * # 它不改变任何判断的诚实度
 *
 * 接回来之前，调用方拿到的仍然是「断了」：`onError` 照常回调、
 * 界面照常撤掉「进行中」。这一层只负责让**「什么时候能重新知道真相」
 * 不再取决于用户记不记得按刷新**，不负责让屏幕好看一点。
 *
 * # 这是这条判断在这个仓库里的唯一一份
 *
 * Lulu 会话流（`screens/lulu/use-stream.ts`）先落地了同一套判断，
 * 它的 `ES_*` 与 `browserGaveUp` 现在都从这里再导出。
 * 抽出来而不是各写一份，理由见 CLAUDE.md §8「抽出一个共享的东西，
 * 不等于用上了它」——三份比两份更糟，两份至少还对称。
 */

/**
 * `EventSource.readyState` 的三档。
 *
 * 不写 `EventSource.OPEN`：jsdom 里没有那个全局，而这几条判断要在
 * 纯函数的用例里跑。数值由 HTML 规范钉死，不会变。
 */
export const ES_CONNECTING = 0
export const ES_OPEN = 1
export const ES_CLOSED = 2

/**
 * 浏览器**已经放弃**这条流了吗。
 *
 * 只有 CLOSED 算。CONNECTING 是「浏览器自己正在接」——那一档我们
 * **一根都不许多开**，插手就是同一条流上两根连接、两份游标。
 */
export function browserGaveUp(readyState: number): boolean {
  return readyState === ES_CLOSED
}

/**
 * 我们自己接回来的退避表（毫秒）。最后一档循环用。
 *
 * 第一档 1 秒：控制面重启只花几秒，一次误伤不该让人等更久。
 * 封顶 8 秒：浏览器自己对「网络层断掉」用的是 3 秒量级，我们比它保守，
 * 而 8 秒也远够不上「跑飞的循环」——十分钟最多 75 次，
 * 那正是「跑 N 轮」那条用例守着的东西。
 */
export const SSE_REVIVE_BACKOFF_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000]

/**
 * 抖动幅度：在那一档基础上再随机加 0 ~ 25%。
 *
 * # 为什么这条流需要它
 *
 * 2026-09-01 那份日志里，**六条流是同一秒一起断的**（控制面容器下线），
 * 于是它们的退避从同一时刻起算、每一档都撞在一起，
 * 一起在第 1 秒、第 3 秒、第 7 秒去敲一个刚要起来的服务端。
 * 而那正是它最脆的时候——一次同步的重试尖峰能把刚起来的进程再打回去。
 *
 * 只往上加不往下减：往下减会让最快的那次比「1 秒」还早，
 * 而那一档本来就是按「控制面重启大概要几秒」定的。
 */
export const SSE_REVIVE_JITTER = 0.25

/**
 * 第 `attempt` 次接回来该等多久。表用完之后一直用最后一档，再加抖动。
 *
 * `random` 可注入是为了让用例能钉死它：不注入的话这个函数没法逐字断言，
 * 而「验一个随机函数」最后总会退化成验区间——区间断言在「抖动被去掉了」
 * 这个方向上是瞎的（去掉之后值仍在区间里）。
 */
export function reviveDelayMs(attempt: number, random: () => number = Math.random): number {
  const index = Math.min(Math.max(attempt, 0), SSE_REVIVE_BACKOFF_MS.length - 1)
  const base = SSE_REVIVE_BACKOFF_MS[index] as number
  return Math.round(base * (1 + SSE_REVIVE_JITTER * random()))
}

/** 挂监听时拿到的两把手柄。 */
export interface ReviveHandle {
  /**
   * 这一根还算不算数。
   *
   * 被我们放弃的那根、以及卸载之后的那根，回调里第一句就该问它。
   * 不问的话，一条迟到的事件能把新连接的状态改掉——
   * 那正是「旧连接污染新状态」的形状。
   */
  readonly owns: () => boolean
  /**
   * **真收到事实了**，把退避清零。
   *
   * 判据刻意是「它改变了什么」而不是「它发生了」：按 `open` 清零的话，
   * 服务端半死不活（连得上、答得出 200，下一秒又塌）时，
   * 每一轮都会把退避打回 1 秒，于是变成每秒一根的重连风暴，
   * **而表面上一切正常**。
   */
  readonly sawFact: () => void
}

export interface RevivingEventSourceOptions {
  /**
   * 每次（重新）连接时现算的地址。
   *
   * 是函数不是字符串：自己接回来的那一根没有 `Last-Event-ID` 可言
   * （那是浏览器在同一根上续传时才会带的头），需要把游标拼进 URL 的
   * 调用方，得在**连接的那一刻**取当前值。
   */
  readonly url: () => string
  readonly withCredentials?: boolean
  /** 挂事件监听。每次接回来都会用新的那一根再调一次。 */
  readonly attach: (source: EventSource, handle: ReviveHandle) => void
  readonly onOpen?: (source: EventSource) => void
  /**
   * 断了。**每一种断法都回调**——包括浏览器自己会接回来的那一种。
   *
   * 调用方据此撤掉「进行中」。它与下面的 `onRevive` 是两件事：
   * 这一条说「现在不知道了」，那一条说「我要重开一根了」。
   */
  readonly onError?: (source: EventSource) => void
  /**
   * 我们**自己**要重开一根之前调一次（浏览器自己重连时不调）。
   *
   * 给需要宣告「位置丢了」的调用方用：新开的 `EventSource` 带不上
   * `Last-Event-ID`，服务端只能按它自己的规则补齐，
   * 而补得全不全调用方比这一层清楚。
   *
   * **它说的是「我打算重开一根」，不是「我接上了」。** 服务端持续不可用时
   * 它会按 1/2/4/8 秒一遍遍地喊，而这一层不知道哪一次会成。
   * 所以要做「恢复之后对齐一次」的调用方，得在这里**留个标记**，
   * 等 `onOpen` 真的来了再兑现——直接在这里做重活，等于把一次恢复动作
   * 放大成一串注定失败的请求。`screens/activity/stream.ts` 就是照这个写的。
   */
  readonly onRevive?: () => void
}

export interface RevivingEventSource {
  /** 停掉：关连接、清退避定时器，之后任何回调都不再算数。 */
  readonly close: () => void
  /** 到目前为止一共开过几根。用例用它断言「真的自己接回来了」。 */
  readonly connections: () => number
}

/**
 * 开一条会自己接回来的 SSE。
 *
 * 卸载或换上下文时调 `close()`：它同时清掉退避定时器——**那一行不是
 * 摆设**，少了它，一个已经没人要的会话会在退避到点时再开一根出来。
 */
export function openRevivingEventSource(
  options: RevivingEventSourceOptions,
): RevivingEventSource {
  let current: EventSource | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  let attempt = 0
  let opened = 0
  let stopped = false

  const clearTimer = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
  }

  const scheduleRevive = () => {
    if (stopped || timer !== undefined) return
    const delay = reviveDelayMs(attempt)
    attempt += 1
    timer = setTimeout(() => {
      timer = undefined
      if (stopped) return
      // 上一根已经 CLOSED，close() 只是把话说全。
      current?.close()
      current = null
      options.onRevive?.()
      connect()
    }, delay)
  }

  function connect(): void {
    if (stopped) return
    const source = new EventSource(options.url(), {
      withCredentials: options.withCredentials ?? true,
    })
    current = source
    opened += 1

    const owns = () => !stopped && source === current
    const handle: ReviveHandle = {
      owns,
      sawFact: () => {
        if (owns()) attempt = 0
      },
    }

    options.attach(source, handle)

    source.onopen = () => {
      if (!owns()) return
      options.onOpen?.(source)
    }
    source.onerror = () => {
      if (!owns()) return
      options.onError?.(source)
      // CONNECTING：浏览器自己在接，我们不插手。
      if (!browserGaveUp(source.readyState)) return
      scheduleRevive()
    }
  }

  connect()

  return {
    close: () => {
      stopped = true
      clearTimer()
      const source = current
      current = null
      source?.close()
    },
    connections: () => opened,
  }
}
